'use client';

import { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Button } from '@/components/ui/button';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { BlogPostsGrid } from '@/components/frontend/blog/blog-posts-grid';
import { BlogPostSkeleton } from '@/components/frontend/blog/blog-post-skeleton';
import type { BlogPost } from '@/lib/api/blog';

export function BlogNewsSection({ initialPosts }: { initialPosts?: BlogPost[] } = {}) {
    const t = useTranslations('homeBlogNews');
    const locale = useLocale();
    const [posts, setPosts] = useState<BlogPost[]>(() => initialPosts ?? []);
    const [isLoading, setIsLoading] = useState(!initialPosts?.length);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (initialPosts?.length) {
            setPosts(initialPosts);
            setIsLoading(false);
            return;
        }
        const fetchPosts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`/api/blog?locale=${locale}&limit=3`);
                if (!response.ok) {
                    throw new Error('Failed to fetch blog posts');
                }
                const data = await response.json();

                const postsArray = data.posts || (Array.isArray(data) ? data : []);

                const transformedPosts: BlogPost[] = postsArray.map((post: any) => ({
                    ...post,
                    content: typeof post.content === 'string' ? (() => {
                        try {
                            const parsed = JSON.parse(post.content);
                            if (Array.isArray(parsed)) {
                                return parsed.join('');
                            }
                            return parsed;
                        } catch {
                            return post.content;
                        }
                    })() : post.content,
                }));

                setPosts(transformedPosts);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch blog posts');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPosts();
    }, [locale, initialPosts?.length]);

    return (
        <SectionWrapper variant="gradient" padding="md" className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-30">
                <div className="absolute left-1/5 top-0 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 space-y-10">
                <SectionHeading
                    pill={t('pill')}
                    title={t('title')}
                    subtitle={t('subtitle')}
                    align="center"
                    maxWidthClass="max-w-3xl"
                    isVisible
                />

                {isLoading ? (
                    <BlogPostSkeleton count={3} />
                ) : error ? (
                    <div className="text-center py-20">
                        <p className="text-red-600 dark:text-red-400">{error}</p>
                    </div>
                ) : (
                    <BlogPostsGrid posts={posts} />
                )}

                <div className="text-center">
                    <Button
                        href={getLocalizedPagePath('blog', locale)}
                        variant="primary"
                        size="md"
                        showArrow
                        className="w-full sm:w-auto  justify-center inline-flex"
                    >
                        {t('viewAll')}
                    </Button>
                </div>
            </div>
        </SectionWrapper>
    );
}

